import { useEffect, useState } from "react";
import { Content } from "@/src/components/wrappers/Content";
import { useFetch } from "@/src/lib/useFetch";
import { cn } from "@/src/lib/utils";
import { Stat } from "../Home/shared";
import { EnquiriesTable } from "./EnquiriesTable";

/** Enquiries screen (Phase C alignment, ws-screens.js): stats row + charter-type
 *  and no-offer filters over the existing table. Source/keyword attribution
 *  columns arrive when PPC data lands (Phase D). */

type EnquiryStats = { newThisWeek: number; builderThisWeek: number; noOffer30: number; withOffer30: number };

const CHARTER_TYPES = [
  ["", "All"],
  ["bareboat", "Bareboat"],
  ["skippered", "Skippered"],
  ["crewed", "Crewed"],
] as const;

export function Enquiries() {
  const { fetch } = useFetch();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [charterType, setCharterType] = useState("");
  const [withoutOffer, setWithoutOffer] = useState(false);
  const [stats, setStats] = useState<EnquiryStats | null>(null);

  // Debounce so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetch<{ data: EnquiryStats }>("/api/enquiries/stats")
      .then((r) => setStats(r.data))
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Content title="Enquiries">
      <div className="mb-2 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat label="New this week" value={stats?.newThisWeek} detail="all sources" />
          <Stat
            label="No offer yet"
            value={stats?.noOffer30}
            detail="last 30 days — need a broker"
            tone={stats && stats.noOffer30 > 0 ? "bad" : "good"}
          />
          <Stat label="Taken to offer" value={stats?.withOffer30} detail="last 30 days" tone="good" />
          <Stat label="Experience builder" value={stats?.builderThisWeek} detail="this week" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {CHARTER_TYPES.map(([value, label]) => (
            <button
              key={value}
              onClick={() => setCharterType(value)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm",
                charterType === value ? "border-blue-600 bg-blue-600 text-white" : "bg-white hover:bg-slate-50",
              )}
            >
              {label}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" />
          <button
            onClick={() => setWithoutOffer((v) => !v)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm",
              withoutOffer ? "border-red-600 bg-red-600 text-white" : "bg-white hover:bg-slate-50",
            )}
          >
            No offer yet
          </button>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, destination…"
            className="ml-auto w-80 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
          />
        </div>
        <EnquiriesTable search={debounced} charterType={charterType || undefined} withoutOffer={withoutOffer} />
      </div>
    </Content>
  );
}
